// =====================================================
// TYPE-SAFE GRAPHQL HOOKS
// Custom hooks for GraphQL operations with TypeScript types
// =====================================================

import { useQuery, useMutation, useSubscription } from '@apollo/client';
import type { QueryOptions, MutationOptions, SubscriptionOptions } from '@apollo/client';
import * as Operations from './operations';

// =====================================================
// TYPE DEFINITIONS
// =====================================================

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface User {
  id: string;
  tenantId: string;
  email: string;
  name: string;
  role: string;
  permissions: string[];
  avatar?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Feature {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  featureType: string;
  config: any;
  status: string;
  version: number;
  parentFeatureId?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface Agent {
  id: string;
  tenantId: string;
  name: string;
  type: string;
  status: string;
  config: any;
  capabilities: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  planType: string;
  status: string;
  settings?: any;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedFeatures {
  items: Feature[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// =====================================================
// AUTHENTICATION HOOKS
// =====================================================

export function useLogin(options?: MutationOptions) {
  return useMutation<
    { login: AuthResponse },
    { email: string; password: string }
  >(Operations.LOGIN_MUTATION, options);
}

export function useRegister(options?: MutationOptions) {
  return useMutation<
    { register: AuthResponse },
    { input: any }
  >(Operations.REGISTER_MUTATION, options);
}

export function useLogout(options?: MutationOptions) {
  return useMutation<{ logout: boolean }>(Operations.LOGOUT_MUTATION, options);
}

export function useMe(options?: QueryOptions) {
  return useQuery<{ me: User }>(Operations.ME_QUERY, options);
}

// =====================================================
// TENANT HOOKS
// =====================================================

export function useCurrentTenant(options?: QueryOptions) {
  return useQuery<{ currentTenant: Tenant }>(
    Operations.GET_CURRENT_TENANT,
    options
  );
}

export function useCreateTenant(options?: MutationOptions) {
  return useMutation<{ createTenant: Tenant }, { input: any }>(
    Operations.CREATE_TENANT_MUTATION,
    options
  );
}

export function useUpdateTenant(options?: MutationOptions) {
  return useMutation<
    { updateTenant: Tenant },
    { id: string; input: any }
  >(Operations.UPDATE_TENANT_MUTATION, options);
}

// =====================================================
// FEATURE HOOKS
// =====================================================

export function useFeatures(
  tenantId: string,
  input?: any,
  options?: QueryOptions
) {
  return useQuery<{ features: PaginatedFeatures }>(
    Operations.GET_FEATURES_QUERY,
    {
      ...options,
      variables: { tenantId, input },
      skip: !tenantId,
    }
  );
}

export function useFeature(id: string, options?: QueryOptions) {
  return useQuery<{ feature: Feature }>(Operations.GET_FEATURE_QUERY, {
    ...options,
    variables: { id },
    skip: !id,
  });
}

export function useCreateFeature(options?: MutationOptions) {
  return useMutation<
    { createFeature: Feature },
    { tenantId: string; input: any }
  >(Operations.CREATE_FEATURE_MUTATION, {
    ...options,
    refetchQueries: ['GetFeatures'],
    awaitRefetchQueries: true,
  });
}

export function useUpdateFeature(options?: MutationOptions) {
  return useMutation<
    { updateFeature: Feature },
    { id: string; input: any }
  >(Operations.UPDATE_FEATURE_MUTATION, {
    ...options,
    refetchQueries: ['GetFeature'],
  });
}

export function useDeleteFeature(options?: MutationOptions) {
  return useMutation<{ deleteFeature: boolean }, { id: string }>(
    Operations.DELETE_FEATURE_MUTATION,
    {
      ...options,
      refetchQueries: ['GetFeatures'],
      awaitRefetchQueries: true,
    }
  );
}

export function useDeployFeature(options?: MutationOptions) {
  return useMutation<any, { input: any }>(
    Operations.DEPLOY_FEATURE_MUTATION,
    options
  );
}

export function useSearchFeatures(
  query: string,
  tenantId: string,
  options?: QueryOptions
) {
  return useQuery<{ searchFeatures: Feature[] }>(
    Operations.SEARCH_FEATURES_QUERY,
    {
      ...options,
      variables: { query, tenantId },
      skip: !query || !tenantId,
    }
  );
}

// =====================================================
// AGENT HOOKS
// =====================================================

export function useAgents(
  tenantId: string,
  type?: string,
  options?: QueryOptions
) {
  return useQuery<{ agents: Agent[] }>(Operations.GET_AGENTS_QUERY, {
    ...options,
    variables: { tenantId, type },
    skip: !tenantId,
  });
}

export function useAgent(id: string, options?: QueryOptions) {
  return useQuery<{ agent: Agent }>(Operations.GET_AGENT_QUERY, {
    ...options,
    variables: { id },
    skip: !id,
  });
}

export function useCreateAgent(options?: MutationOptions) {
  return useMutation<
    { createAgent: Agent },
    { tenantId: string; input: any }
  >(Operations.CREATE_AGENT_MUTATION, {
    ...options,
    refetchQueries: ['GetAgents'],
  });
}

export function useUpdateAgent(options?: MutationOptions) {
  return useMutation<
    { updateAgent: Agent },
    { id: string; config: any }
  >(Operations.UPDATE_AGENT_MUTATION, {
    ...options,
    refetchQueries: ['GetAgent'],
  });
}

export function useDeleteAgent(options?: MutationOptions) {
  return useMutation<{ deleteAgent: boolean }, { id: string }>(
    Operations.DELETE_AGENT_MUTATION,
    {
      ...options,
      refetchQueries: ['GetAgents'],
    }
  );
}

export function useRestartAgent(options?: MutationOptions) {
  return useMutation<{ restartAgent: Agent }, { id: string }>(
    Operations.RESTART_AGENT_MUTATION,
    options
  );
}

// =====================================================
// EXECUTION HOOKS
// =====================================================

export function useExecutions(
  tenantId: string,
  featureId?: string,
  status?: string,
  pagination?: any,
  options?: QueryOptions
) {
  return useQuery<any>(Operations.GET_EXECUTIONS_QUERY, {
    ...options,
    variables: { tenantId, featureId, status, pagination },
    skip: !tenantId,
  });
}

export function useExecuteFeature(options?: MutationOptions) {
  return useMutation<any, { featureId: string; inputData?: any }>(
    Operations.EXECUTE_FEATURE_MUTATION,
    {
      ...options,
      refetchQueries: ['GetExecutions'],
    }
  );
}

export function useCancelExecution(options?: MutationOptions) {
  return useMutation<{ cancelExecution: boolean }, { id: string }>(
    Operations.CANCEL_EXECUTION_MUTATION,
    options
  );
}

// =====================================================
// DEPLOYMENT HOOKS
// =====================================================

export function useDeployments(
  featureId: string,
  environment?: string,
  options?: QueryOptions
) {
  return useQuery<any>(Operations.GET_DEPLOYMENTS_QUERY, {
    ...options,
    variables: { featureId, environment },
    skip: !featureId,
  });
}

export function useRollbackDeployment(options?: MutationOptions) {
  return useMutation<any, { deploymentId: string }>(
    Operations.ROLLBACK_DEPLOYMENT_MUTATION,
    options
  );
}

// =====================================================
// ANALYTICS HOOKS
// =====================================================

export function useAnalytics(
  tenantId: string,
  timeframe: string,
  featureId?: string,
  options?: QueryOptions
) {
  return useQuery<any>(Operations.GET_ANALYTICS_QUERY, {
    ...options,
    variables: { tenantId, timeframe, featureId },
    skip: !tenantId || !timeframe,
  });
}

export function useUsageMetrics(
  tenantId: string,
  period: string,
  options?: QueryOptions
) {
  return useQuery<any>(Operations.GET_USAGE_METRICS_QUERY, {
    ...options,
    variables: { tenantId, period },
    skip: !tenantId || !period,
  });
}

// =====================================================
// SYSTEM HOOKS
// =====================================================

export function useSystemHealth(options?: QueryOptions) {
  return useQuery<any>(Operations.GET_SYSTEM_HEALTH_QUERY, {
    ...options,
    pollInterval: 30000, // Poll every 30 seconds
  });
}

// =====================================================
// SUBSCRIPTION HOOKS
// =====================================================

export function useFeatureUpdatedSubscription(
  tenantId: string,
  featureId?: string,
  options?: SubscriptionOptions
) {
  return useSubscription<any>(Operations.FEATURE_UPDATED_SUBSCRIPTION, {
    ...options,
    variables: { tenantId, featureId },
    skip: !tenantId,
  });
}

export function useFeatureCreatedSubscription(
  tenantId: string,
  options?: SubscriptionOptions
) {
  return useSubscription<{ featureCreated: Feature }>(
    Operations.FEATURE_CREATED_SUBSCRIPTION,
    {
      ...options,
      variables: { tenantId },
      skip: !tenantId,
    }
  );
}

export function useFeatureDeletedSubscription(
  tenantId: string,
  options?: SubscriptionOptions
) {
  return useSubscription<{ featureDeleted: string }>(
    Operations.FEATURE_DELETED_SUBSCRIPTION,
    {
      ...options,
      variables: { tenantId },
      skip: !tenantId,
    }
  );
}

export function useAgentStatusChangedSubscription(
  tenantId: string,
  agentId?: string,
  options?: SubscriptionOptions
) {
  return useSubscription<any>(Operations.AGENT_STATUS_CHANGED_SUBSCRIPTION, {
    ...options,
    variables: { tenantId, agentId },
    skip: !tenantId,
  });
}

export function useDeploymentProgressSubscription(
  deploymentId: string,
  options?: SubscriptionOptions
) {
  return useSubscription<any>(Operations.DEPLOYMENT_PROGRESS_SUBSCRIPTION, {
    ...options,
    variables: { deploymentId },
    skip: !deploymentId,
  });
}

export function useExecutionUpdatedSubscription(
  featureId: string,
  options?: SubscriptionOptions
) {
  return useSubscription<any>(Operations.EXECUTION_UPDATED_SUBSCRIPTION, {
    ...options,
    variables: { featureId },
    skip: !featureId,
  });
}

export function useCollaborationEventSubscription(
  featureId: string,
  options?: SubscriptionOptions
) {
  return useSubscription<any>(Operations.COLLABORATION_EVENT_SUBSCRIPTION, {
    ...options,
    variables: { featureId },
    skip: !featureId,
  });
}

export function useCursorMovedSubscription(
  featureId: string,
  options?: SubscriptionOptions
) {
  return useSubscription<any>(Operations.CURSOR_MOVED_SUBSCRIPTION, {
    ...options,
    variables: { featureId },
    skip: !featureId,
  });
}

export function useNotificationSubscription(
  userId: string,
  options?: SubscriptionOptions
) {
  return useSubscription<any>(Operations.NOTIFICATION_SUBSCRIPTION, {
    ...options,
    variables: { userId },
    skip: !userId,
  });
}

// =====================================================
// OPTIMISTIC UPDATE HELPERS
// =====================================================

export function createOptimisticFeature(input: any): Feature {
  return {
    id: `temp-${Date.now()}`,
    tenantId: input.tenantId || '',
    name: input.name,
    description: input.description,
    featureType: input.featureType,
    config: input.config,
    status: 'DRAFT',
    version: 1,
    createdBy: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    __typename: 'Feature',
  };
}

export function createOptimisticAgent(input: any): Agent {
  return {
    id: `temp-${Date.now()}`,
    tenantId: input.tenantId || '',
    name: input.name,
    type: input.type,
    status: 'DEPLOYING',
    config: input.config,
    capabilities: input.capabilities || [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    __typename: 'Agent',
  };
}
